import { Button as MuiButton, createTheme, ThemeProvider } from "@mui/material";
import { MagnifyingGlass, Pencil } from "phosphor-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useFilter, useGetFilters } from "../../api/admin/filter";
import DialogBox from "../DialogBox";
import DriversListFiltersModal from "./DriversListFiltersModal";
import DriverListFiltersDropdown from "./DriverListFiltersDropdown";
import DriverListSaveEditFilter from "./DriverListSaveEditFilter";
import { type DriversListFiltersFormValues } from "./DriversListFiltersForm/DriversListFiltersFormValues";
import { useSavedFilters } from "./useSavedFilters";
import { useLocation } from "react-router-dom";
import {
    type DriverCategoryOptionsType,
    driverPageLocation,
} from "../../utils/driverPageLocation";
import FormTextInput from "../FormTextInput";
import { driverListFiltersDefaultValues } from "./DriversListFiltersForm/driversListFiltersDefaultValues";
import useTableStore from "../../stores/useTableStore";
import { ApiParsedFiltersResponse } from "../../models/api/admin/filter";

export type DriversListFiltersProps = {
    filterModalOpen: "add" | "edit" | false;
    setFilterModalOpen: Dispatch<SetStateAction<false | "add" | "edit">>;
    handleFetchDrivers: (
        filters?: DriversListFiltersFormValues,
        filterId?: string,
        noSave?: boolean
    ) => Promise<boolean>;
    isFetchingDrivers: boolean;
    query: string | null;
    setQuery: (value: string | null) => void;
};

const DriversListFilters: React.FC<DriversListFiltersProps> = ({
    filterModalOpen,
    setFilterModalOpen,
    handleFetchDrivers,
    isFetchingDrivers,
    query,
    setQuery,
}) => {
    const filtersTheme = createTheme({
        typography: {
            fontFamily: "Inter",
        },
        palette: {
            primary: {
                main: "#1B1C17",
            },
        },
    });

    const pageLocation = useLocation();
    const location = driverPageLocation(
        pageLocation.pathname
    ) as DriverCategoryOptionsType;
    const getAppliedFilter = useTableStore((state) => state.getAppliedFilter);
    const appliedFilter = getAppliedFilter(location);

    const setAppliedFilter = useTableStore((state) => state.setAppliedFilter);

    const {
        filter: fetchedFilter,
        isAddingFilter,
        isEditingFilter,
        isDeletingFilter,
    } = useFilter(appliedFilter?.filterId ?? null);

    const {
        filters: savedFilters,
        filtersError: savedFiltersError,
        isFiltersLoading: isSavedFiltersLoading,
        refetchFilters: refetchSavedFilters,
    } = useGetFilters();

    const [isEdit, setIsEdit] = useState(false);
    const [canEditFilter, setCanEditFilter] = useState(false);

    // remembers form fields when adding a filter and going back to change something before saving
    const [selectedFilter, setSelectedFilter] =
        useState<DriversListFiltersFormValues>();

    const prefilledFormValues = fetchedFilter?.filterId
        ? fetchedFilter
        : appliedFilter?.filterFields && {
              filterId: "",
              name: "",
              filterDetails: appliedFilter.filterFields,
              userIds: [],
          };

    const filterDefaultValues = driverListFiltersDefaultValues(location);

    const handleEditMode = (edit: boolean) => {
        setIsEdit(edit);
        if (edit && fetchedFilter) {
            setSelectedFilter(fetchedFilter.filterDetails);
        } else {
            setSelectedFilter(filterDefaultValues);
        }
    };

    const handleOpenFilters = () => {
        setFilterModalOpen("add");
        handleEditMode(false);
    };

    const handleOpenEditDialog = () => {
        setFilterModalOpen("edit");
        handleEditMode(true);
    };

    const handleCloseEditDialog = () => {
        setFilterModalOpen(false);
        handleEditMode(false);
    };

    const handleEditFilterProperties = () => {
        setFilterModalOpen("add");
    };

    const handleApplyFilter = (filters: ApiParsedFiltersResponse) => {
        if (filters) {
            setAppliedFilter(
                {
                    filterId: filters.filterId ?? "",
                    filterFields: filters.filterDetails ?? filterDefaultValues,
                    name: filters.name ?? "",
                },
                location
            );
        }
    };

    const filterValues =
        selectedFilter !== filterDefaultValues
            ? selectedFilter
            : appliedFilter.filterFields;

    const {
        handleSaveFilter,
        handleSubmitNewFilter,
        handleSubmitEditedFilter,
        handleDeleteFilter,
    } = useSavedFilters(
        isEdit,
        filterValues,
        setSelectedFilter,
        setFilterModalOpen,
        refetchSavedFilters,
        handleFetchDrivers,
        handleEditMode,
        location
    );

    useEffect(() => {
        setCanEditFilter(
            JSON.stringify(appliedFilter.filterFields) !==
                JSON.stringify(filterDefaultValues)
        );
    }, [appliedFilter]);

    const handleClearFilters = () => {
        setAppliedFilter(
            {
                filterId: "",
                filterFields: filterDefaultValues,
            },
            location
        );
    };

    return (
        <div className="flex w-full flex-col flex-wrap items-center justify-start gap-6 md:flex-row">
            <FormTextInput
                StartAdornmentIcon={MagnifyingGlass}
                placeholder={"Search"}
                className="w-full md:w-[320px]"
                value={query ?? ""}
                onChange={(e) => setQuery(e.target.value ?? null)}
                disabled={isFetchingDrivers}
            />
            <div
                className={`w-full justify-start rounded-sm border-2 md:w-fit 
                    ${
                        canEditFilter
                            ? "border-S2D-light-green-80 bg-S2D-dark-green-70.1 "
                            : "border-S2D-neutral-10 "
                    }`}
            >
                <ThemeProvider theme={filtersTheme}>
                    <div className="flex min-w-fit flex-row justify-between">
                        <DriverListFiltersDropdown
                            onOpenFilters={handleOpenFilters}
                            savedFilters={savedFilters}
                            onApplyFilter={handleApplyFilter}
                            onClearFilter={handleClearFilters}
                            appliedFilter={fetchedFilter}
                            filtersError={savedFiltersError}
                            isFiltersLoading={isSavedFiltersLoading}
                        />
                        <MuiButton
                            id="edit-filters-button"
                            data-testid="edit-filters-button"
                            aria-haspopup="true"
                            disableRipple
                            disabled={!canEditFilter}
                            onClick={handleOpenEditDialog}
                            sx={{
                                padding: 0,
                                borderLeft: 2,
                                borderColor: `${
                                    canEditFilter
                                        ? "var(--secondary)"
                                        : "#1B1C17"
                                }`,
                                borderRadius: 0,
                            }}
                        >
                            <div className="flex h-12 items-center">
                                <Pencil size={24} />
                            </div>
                        </MuiButton>
                    </div>
                </ThemeProvider>
            </div>
            <DialogBox
                isOpen={filterModalOpen === "add"}
                onClose={() => setFilterModalOpen(false)}
                title="Filters"
                size="xl"
                closeButton
            >
                <DriversListFiltersModal
                    isEditingFilter={isEdit}
                    selectedFilter={selectedFilter}
                    onSaveFilter={handleSaveFilter}
                    handleCloseEditDialog={handleCloseEditDialog}
                    isApplyingFilter={isFetchingDrivers}
                    appliedFilter={appliedFilter}
                />
            </DialogBox>
            <DialogBox
                isOpen={filterModalOpen === "edit"}
                title={isEdit ? "Edit filter" : "Save filter"}
                size="xl"
                closeButton
                onClose={() => handleCloseEditDialog()}
            >
                <DriverListSaveEditFilter
                    selectedFilter={selectedFilter}
                    onEditFilter={handleEditFilterProperties}
                    onSubmitNewFilter={handleSubmitNewFilter}
                    onSubmitEditedFilter={handleSubmitEditedFilter}
                    isEdit={isEdit}
                    isAddingFilter={isAddingFilter}
                    isEditingFilter={isEditingFilter}
                    handleCloseEditDialog={handleCloseEditDialog}
                    onDeleteFilter={handleDeleteFilter}
                    isDeletingFilter={isDeletingFilter}
                    appliedFilter={prefilledFormValues}
                    onApplyFilter={handleFetchDrivers}
                />
            </DialogBox>
        </div>
    );
};

export default DriversListFilters;
